import React, { useState } from 'react';
import { observer } from 'mobx-react';
import styled, { keyframes } from 'styled-components';
import { Box, Button, Flex, Heading, Icon, Paragraph, Text } from 'core/components';
import { FormComponent } from 'core/form';
import type BoardModel from 'app/stores/boards/BoardModel';
import { VariableFieldState } from 'app/stores/ude/variables.types';
import { TbChevronRight } from 'react-icons/tb';
import BoardVariableSettings from './BoardVariableSettings';
import BoardVariableMapping from './BoardVariableMapping';

interface Props {
  board: BoardModel;
  onClose?: () => void;
}

// Form fields scoped to just the dashboard variables (filters). Mirrors the
// definitions in BoardSettings for these same fields so the form behaves the
// same way, but keeps the filters drawer fully decoupled from board settings.
const filterFields = {
  'config.variables': {
    isComplexArray: true,
    defaultValue: []
  },
  'config.variables[].type': {
    label: 'Type',
    defaultValue: 'custom',
    options: [
      { value: 'guided', label: 'Guided' },
      { value: 'custom', label: 'Custom' }
    ]
  },
  'config.variables[].name': {
    label: 'Filter Name',
    required: true,
    defaultValue: '',
    placeholder: 'The display label for this filter'
  },
  'config.variables[].field': {
    label: 'Field',
    defaultValue: ''
  },
  'config.variables[].value': {
    label: 'Default Value',
    required: true,
    defaultValue: null
  },
  'config.variables[].enabled': {
    label: 'Enabled',
    defaultValue: true
  },
  'config.variables[].allowMulti': {
    label: 'Multiple selections',
    defaultValue: true
  },
  'config.variables[].exactMatch': {
    label: 'Force exact match',
    defaultValue: false
  },
  'config.variables[].operator': {
    label: 'Operator',
    defaultValue: '='
  },
  'config.variables[].options': {
    label: 'Options',
    isComplexArray: true,
    defaultValue: []
  },
  'config.variables[].options[].label': {
    label: 'Option Label',
    defaultValue: ''
  },
  'config.variables[].options[].value': {
    label: 'Option Value',
    required: true
  }
};

const slideInRight = keyframes`
  from {
    transform: translateX(24px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideInLeft = keyframes`
  from {
    transform: translateX(-24px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const AnimatedPane = styled(Box)<{ $direction: 'forward' | 'back' }>`
  animation: ${({ $direction }) => ($direction === 'forward' ? slideInRight : slideInLeft)} 200ms ease;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const BreadcrumbButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  font-weight: 400;
  &:hover {
    text-decoration: underline;
  }
`;

const BoardFiltersDrawer = observer(({ board, onClose }: Props) => {
  const [mappingVariable, setMappingVariable] = useState<VariableFieldState | null>(null);
  // Track which direction we're animating into the next pane.
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');

  const handleMapVariable = (variable: VariableFieldState) => {
    setDirection('forward');
    setMappingVariable(variable);
  };

  const handleBack = () => {
    setDirection('back');
    setMappingVariable(null);
  };

  const handleFormChange = ({ formValues }: { formValues: { config?: Record<string, unknown> } }) => {
    const config = { ...board.get('config'), ...(formValues.config || {}) };
    board.safeSet({ config });
  };

  return (
    <FormComponent
      fields={filterFields}
      model={board}
      options={{ name: 'board-filters-form' }}
      onChange={handleFormChange}
    >
      {() => (
        <Flex flexDirection="column" gap={2} py={3} px={2} width={535} height="100%" overflow="hidden">
          {/* Header / breadcrumb */}
          <Flex alignItems="center" gap="6px" minHeight={28}>
            {mappingVariable ? (
              <Heading level={5} m={0} display="flex" alignItems="center">
                <BreadcrumbButton type="button" onClick={handleBack}>
                  Filters
                </BreadcrumbButton>
                <Box mx="4px" display="flex" alignItems="center">
                  <Icon icon={TbChevronRight} size={16} color="muted" />
                </Box>
                <Text>Map {mappingVariable.name.value} to Panels</Text>
              </Heading>
            ) : (
              <Heading level={5} m={0}>
                Filters
              </Heading>
            )}
            {onClose && <Button ml="auto" small minimal icon="cross" onClick={onClose} aria-label="Close filters" />}
          </Flex>

          <Box flex={1} overflow="auto" mx={-2} px={2} key={mappingVariable ? 'mapping' : 'list'}>
            {mappingVariable ? (
              <AnimatedPane $direction={direction}>
                <BoardVariableMapping board={board} variable={mappingVariable} />
              </AnimatedPane>
            ) : (
              <AnimatedPane $direction={direction}>
                <Paragraph color="muted" mt={0} mb={2}>
                  Filters are mapped automatically to the proper dimension in each panel. To filter by a variable, or a
                  different dimension, choose <strong>Custom Filter</strong>.
                </Paragraph>
                <BoardVariableSettings board={board} onMapVariable={handleMapVariable} />
              </AnimatedPane>
            )}
          </Box>
        </Flex>
      )}
    </FormComponent>
  );
});

export default BoardFiltersDrawer;
